import type { Hit } from 'instantsearch.js';
import type { PropsWithChildren } from 'react';

import type { PriceDisplayMode } from '~/utilities/graphql/codegen';

export enum ProductMediaTypeEnum {
  E01 = 'E01',
  E02 = 'E02',
  E04 = 'E04',
  F01 = 'F01',
  F02 = 'F02',
  F04 = 'F04',
  M01 = 'M01',
  M02 = 'M02',
  M03 = 'M03',
  M04 = 'M04',
  M06 = 'M06',
  M07 = 'M07',
  Z01 = 'Z01',
  Z02 = 'Z02',
  Z03 = 'Z03',
  Z04 = 'Z04',
  Z06 = 'Z06',
  Z07 = 'Z07',
}

type ProductMediaTypeEnumKeys = keyof typeof ProductMediaTypeEnum;

export interface SimplifiedImage {
  url: string;
  altText: string;
  version: string;
  productImageMediaTypeEnum: ProductMediaTypeEnum;
  base64BackgroundImage?: string | null;
  seoDescription: string | null;
  isAI?: boolean;
}

export type SimplifiedImageJSON = {
  [key in ProductMediaTypeEnum]: SimplifiedImage;
};

export interface MultiSigningJSON {
  alternativeLabel?: string;
  code: string | null;
  discountSigning: boolean;
  image: string | null;
  label: string;
  labelCssClasses: string;
  labelHtmlSafe: string | null;
  membersOnlySigning: boolean;
  personalisedPrice?: number;
  plpPositionClass: string;
  priority: number;
  productSigningBackgroundColour: string | null;
  productSigningTextColour: string | null;
  renderAsText: boolean | null;
  showOnPdp: boolean;
  showOnPlp: boolean;
  type: string;
}

interface FiberCompositionsJSON {
  code: string;
  descriptionLong: string | null;
  descriptionShort: string;
  percentage: number;
  ranking: string;
  score: number;
  type: string | null;
}

/**
 * if the current language is English the property will be a string containing the English value
 *
 * if the current language is other than English, the property will be an object containing both
 * English and the current language values
 *
 * @example
 * ```ts
 * {
 *  EN: 'some string value',
 *  NL: 'bepaalde stringwaarde',
 * }
 * ```
 */
export type LocaleStringObject = string | { EN: string; [languageCode: string]: string };

export interface ProductHit {
  stockNotificationMessage: LocaleStringObject;
  'baseProduct.stockNotificationMessage': LocaleStringObject;
  marginClass: number;
  percentageDiscount: string;
  stockQuantity: number;
  'baseProduct.stockQuantity': number;
  gender: string;
  length: string;
  fiberCompositionsJson: FiberCompositionsJSON[];
  staticCategoryPath: string;
  daysOnline: number;
  subTargetAudience: string;
  category: string[];
  fabricBulletList: string[];
  detailsBulletList: string[];
  fit: LocaleStringObject;
  exactMainColorDescription: string;
  'approvalStatus.code': string;
  program: LocaleStringObject;
  neverOutOfStock: boolean;
  'baseProduct.code': string;
  exactColorDescription: LocaleStringObject;
  fabric: string;
  fabricLongDescription: string;
  fullLength: string;
  materialgroup: LocaleStringObject;
  hasFixedPriceDiscount: boolean;
  detailsTitle: string;
  comingSoon: boolean;
  alwaysShowOnLister: boolean;
  waistHeight: string;
  fabricTitleText: string;
  'baseProduct.approvalStatus.code': string;
  collection: string;
  styleCode: string;
  technicalName: string;
  salesClassification: string;
  facetColorStyle: string;
  fabricType: string;
  stretch: string;
  fiberRankingScore: number;
  fiberRanking: string;
  lastSeason: string;
  discountPercentageInt: number;
  colour: LocaleStringObject;
  colorCode: string;
  ean: string;
  multiSigningJson: MultiSigningJSON[];
  simplifiedImageJson: SimplifiedImageJSON;
  mccValue: string;
  'baseProduct.stockLevelStatus': string;
  stock_NL01: string;
  personalisedPrice: string;
  price: string;
  priceValue: number;
  personalisedPriceValue?: number;
  lowestPrice?: number;
  priceDisplayMode?: PriceDisplayMode | null;
  url: string;
  'baseProduct.onlineDate': number;
  'baseProduct.visible': boolean;
  'baseProduct.offlineDate': number;
  'baseProduct.available': boolean;
  signingJson: string;
  productTypes: string[];
  size: LocaleStringObject;
  'img-zoom_front_Listerlarge': string;
  'img-front_Listerlarge': string;
  'baseProduct.onlineSales': number;
  'baseProduct.averageDailyOnlineSales': number;
  staticProductUrlsJson: LocaleStringObject;
  categoryNameFacet: string[];
  fits: string;
  fromPriceValue: number;
  analyticsCategory: string;
  'baseProduct.daysOnline': number;
  stockLevelStatus: string;
  name: LocaleStringObject;
  code: string;
  staticCategoryPathIds: string;
  intermediaryPriceValue: number;
  oneSize?: boolean;
  isGiftCard?: boolean;
  jeansSize: LocaleStringObject;
  style: LocaleStringObject;
  swatch?: string;
  /**
   * Flag if product is marked(manually) as Rule-Based Personalization Product
   */
  isRBP?: boolean;
}

type simplifiedImageJsonKey = keyof Pick<ProductHit, 'simplifiedImageJson'>;
type SimplifiedImageKeys = keyof SimplifiedImage;

export type SimplifiedImageJsonAttributes =
  `${simplifiedImageJsonKey}.${ProductMediaTypeEnumKeys}.${SimplifiedImageKeys}`;

export type AlgoliaProductHit = Hit<ProductHit>;
export type ProductHitKeys = keyof ProductHit;
export type ProductHitTypes = keyof ProductHit[ProductHitKeys];

export interface InjectedContentData {
  positionDesktop?: number;
  positionTablet?: number;
  positionMobile?: number;
  spanDesktop?: number;
  spanTablet?: number;
  spanMobile?: number;
}

export interface StyledInjectedContent {
  $desktopRow?: number;
  $desktopColumn?: string;
  $desktopSpan?: number;
  $desktopOnLastRow: boolean;
  $tabletRow?: number;
  $tabletColumn?: string;
  $tabletSpan?: number;
  $tabletOnLastRow: boolean;
  $mobileRow?: number;
  $mobileColumn?: string;
  $mobileSpan?: number;
  $mobileOnLastRow: boolean;
  $shouldShowMobile: boolean;
  $shouldShowTablet: boolean;
  $shouldShowDesktop: boolean;
}

export type InjectedContentProps = PropsWithChildren<InjectedContentData>;

// TODO Hard Coded interface for parent facet
export interface ParentFacet {
  text: string;
  attribute: string;
  valueEN: string;
}

export interface AlgoliaSuggestionHit {
  objectID: string;
  query: string;
  popularity: number;
}
